// auth code that's not directly relevant for redux store

import { AuthenticationResult, BrowserCacheLocation, CacheLookupPolicy, Configuration, PublicClientApplication, SilentRequest } from "@azure/msal-browser";
import { get, set, has } from "lodash-es";
import { getAppVersionHeader, getSessionId, LOGGED_OUT_URI, MVISION_AUTHORITY } from "../environments";
import { QUERY_PARAM_SESSION_ID } from "./backend-api-interface";
import { BackendFetchOptions, defaultBackendFetchOptions } from "./cloud-backend-auth";
import { getLatestActiveMsalAccount, setLatestActiveMsalAccount } from "../util/local-storage";
import { timeoutSignal } from "../util/timeout-signal";
import { retryWithBackoff } from "../util/retry";
import { AppVersionInfo } from "../store/appVersion/appVersionTypes";
import routes, { doRedirect } from '../routes';
import { AuthInteractionRequiredError } from "../util/errors";
import { getOidcSettings, isOidcEnabled } from "./oidc-settings";
import { OidcProvider } from "./oidc-provider";

const VERBOSE_LOGGING = false;

const REDIRECT_AFTER_LOGIN = 'redirect_after_login';
const LOGIN_REDIRECT_URI = window.location.origin;
const LOGOUT_REDIRECT_URI = window.location.origin + '/#' + LOGGED_OUT_URI;
const TOKEN_REDIRECT_URI = window.location.origin + '/auth.html';

/**
 * Get a string state for current route for redirecting user back to this page after MSAL auth.
 * Actual page state is NOT preserved, only the URL where we're redirecting to.
 * Current page must be a valid route, or a best guess for one at least.
 */
const getCurrentRouteState = () => {
  const currentRoute = window.location.pathname + window.location.hash;
  const matchingRoute = Object.values(routes)
    // filter out the root route
    .filter(r => r.slug !== '/')
    // find a matching route, or close enough in case we have query parameters etc
    // TODO: this will not work as expected if we end up having sub-pages where the common
    // non-root route slug is also a valid page in itself, but as of now this is not relevat
    .find(r => currentRoute.includes(r.slug));
  const hasMatchingRoute = matchingRoute !== undefined;

  if (hasMatchingRoute) {
    // note: we send back the full matching URL (possible sub path + /#/ + route), not just the route
    return `${REDIRECT_AFTER_LOGIN}=${btoa(currentRoute)}`;
  } else {
    return undefined;
  }
}

/** Details of a user after they have authenticated through MSAL. */
export type LoggedInMsalUser = {
  userId: string;
  name?: string;
  email?: string;
  fullName: string;
};

/** Models authentication to an azure app registration. Do not put into redux store! */
export default class AppAuth {

  /** Name of the app registration */
  appName: string;

  /** Client ID matching the azure app registration */
  clientId: string;

  /** MSAL configuration object */
  config: Configuration;

  /** The set of scopes that we ask for when requesting tokens */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request: any;

  /** MSAL instance object */
  msalInstance: PublicClientApplication | null;

  /**
   * Identity-broker provider, used instead of MSAL when config.json supplies
   * OIDC settings. Created lazily: config.json is fetched asynchronously, so it
   * is not available when this class is constructed at module load.
   */
  private oidcProvider: OidcProvider | null = null;

  /** True if user has logged into this app registration, false otherwise */
  isLoggedIn: boolean;

  /** Name of the user who has logged in with this app auth. */
  loggedInUser: LoggedInMsalUser | undefined;

  /** The version of the app that this app auth is sending requests for. */
  _appVersion: AppVersionInfo | undefined;

  constructor(appName: string, clientId: string) {
    this.appName = appName;
    this.clientId = clientId;

    this.isLoggedIn = false;

    this.request = {
      scopes: [`${clientId}/.default`],
    };

    this.config = {
      auth: {
        clientId: this.clientId,
        authority: MVISION_AUTHORITY,
      },
      cache: {
        cacheLocation: BrowserCacheLocation.LocalStorage,
      },
      // system: {
      //     // uncomment this if needed
      //     loggerOptions: {
      //         loggerCallback: (logLevel: LogLevel, message: string, containsPii: boolean) => console.log(`${this.appName}: ${message}`),
      //         logLevel: LogLevel.Info,
      //         // change this to true if needed -- don't commit or deploy into production
      //         piiLoggingEnabled: false,
      //     },
      // },

    };

    this.msalInstance = null;
  }

  setAppVersion(appVersion: AppVersionInfo) {
    this._appVersion = appVersion;
  }

  private verboseLog(message: string) {
    if (VERBOSE_LOGGING) { console.log(message); }
  }

  private setLatestActiveAccount() {
    if (this.msalInstance) {
      const account = this.msalInstance.getActiveAccount();
      if (account) {
        this.verboseLog(`Setting latest active MSAL account (${account.username}) in localStorage`);
        setLatestActiveMsalAccount(account.homeAccountId);
        return;
      }
    }

    throw new Error('Could not set latest MSAL account');
  }

  /**
   * The identity-broker provider when the broker path is configured, else null.
   * Resolved on first use rather than in the constructor.
   */
  private getOidcProvider(): OidcProvider | null {
    if (!isOidcEnabled()) { return null; }
    if (this.oidcProvider === null) {
      const settings = getOidcSettings();
      if (!settings) { return null; }
      this.oidcProvider = new OidcProvider(settings);
    }
    return this.oidcProvider;
  }

  /** Log in using REDIRECT flow. */
  async logIn() {
    const oidc = this.getOidcProvider();
    if (oidc) {
      // Broker path. Completes a callback if one is in flight, otherwise starts
      // an interactive login; `signinRedirect` navigates away and does not return.
      const appState = await oidc.handleRedirectCallback();
      if (appState) { await doRedirect(atob(appState)); }

      const user = await oidc.getUser();
      if (!user) {
        const state = getCurrentRouteState();
        const encoded = state ? btoa(window.location.pathname + window.location.hash) : undefined;
        await oidc.logIn(encoded);
        return;
      }

      this.loggedInUser = await oidc.getLoggedInUserDetails();
      this.isLoggedIn = true;
      return;
    }

    if (this.msalInstance === null) {
      this.msalInstance = new PublicClientApplication(this.config);
      await this.msalInstance.initialize();
    }

    // we need to handle redirect promise here for redirect log-in and log-out processes
    const result = await this.msalInstance.handleRedirectPromise({ navigateToLoginRequestUrl: false });

    // update active account based on redirect result
    if (result?.account) {
      this.verboseLog(`Setting active account based on redirect result: ${result.account.username}`);
      this.msalInstance.setActiveAccount(result.account);
      this.setLatestActiveAccount();
    }

    // check if we have a redirect state to go to after login
    if (result?.state) {
      const values = result.state.split('=');
      const redirectIndex = values.findIndex(v => v === REDIRECT_AFTER_LOGIN);
      if (redirectIndex !== -1) {
        // the value of this state prop should be in the next item
        await doRedirect(atob(values[redirectIndex + 1]));
      }
    }

    if (!this.isLoggedIn) {

      /** Try getting an access token to check that we're ACTUALLY logged in */
      const tryGettingAccessTokenAndSetLoggedIn = async () => {
        this.verboseLog('Getting an access token with cached credentials');
        await this.getAccessToken(true);
        try {
          this.setLatestActiveAccount();
        }
        catch (err) {
          // reset cache if we have localStorage auth trash left from a previous version
          if ((get(err, 'errorCode', '')).includes('multiple_matching_tokens')) {
            await this.getAccessToken(true, true);
          } else {
            throw err;
          }
        }

        this.setLoggedIn();
      };

      // First see if we already have exactly one set of cached credentials
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length === 1) {
        this.verboseLog('Already logged in, using cached credentials');
        this.msalInstance.setActiveAccount(accounts[0]);

        try {
          await tryGettingAccessTokenAndSetLoggedIn();
          return;
        } catch (err) {
          this.verboseLog('Did not get access token with cached credentials');
          console.error(err);
        }
      }

      // If not, try to see if we already have an active account
      const latestAccount = getLatestActiveMsalAccount();
      if (latestAccount) {
        this.verboseLog('Found latest active MSAL account entry -- checking if that\'s still logged in');

        try {
          const account = this.msalInstance.getAccount({ homeAccountId: latestAccount });
          if (account) {
            this.verboseLog(`Trying to use an existing active account ${account.username}`);
            this.msalInstance.setActiveAccount(account);
            await tryGettingAccessTokenAndSetLoggedIn();
            return;
          }
        }
        catch (err) {
          this.verboseLog('Did not get access token with existing active account');
          console.log(err);
        }
      }

      // Fall back to regular redirect auth login flow
      try {
        // this will (on success) always redirect user away from current page and stop
        // any of the rest of the code from being run
        this.verboseLog('Falling back to regular redirect auth login');
        await this.msalInstance.loginRedirect({
          scopes: this.request.scopes,
          prompt: 'select_account',
          redirectUri: LOGIN_REDIRECT_URI,
          state: getCurrentRouteState()
        });
      }
      catch (err) {
        console.error(err);
        throw err;
      }
    }

    this.isLoggedIn = true;
    this.setLoggedIn();
  }

  /** Log out using REDIRECT flow. */
  async logOut() {
    const oidc = this.getOidcProvider();
    if (oidc) {
      if (this.isLoggedIn) {
        // RP-initiated logout: ends the broker session, not just the local one.
        await oidc.logOut(LOGOUT_REDIRECT_URI);
      }
      this.isLoggedIn = false;
      return;
    }

    if (this.msalInstance && this.isLoggedIn) {
      // No user signed in
      try {
        // this will (on success) always redirect user away from current page and stop
        // any of the rest of the code from being run
        await this.msalInstance.logoutRedirect({
          postLogoutRedirectUri: LOGOUT_REDIRECT_URI,
        });
      }
      catch (err) {
        console.error(err);
        throw err;
      }
    }

    this.isLoggedIn = false;
  }

  private async getAccessToken(firstTime: boolean = false, skipCache: boolean = false): Promise<string> {
    const oidc = this.getOidcProvider();
    if (oidc) {
      try {
        // Renewal happens via the refresh token; no iframe is involved.
        return await oidc.getAccessToken();
      } catch (err) {
        // Same contract as the MSAL path: signal that interaction is needed so
        // the caller can open a new tab, rather than redirecting away and
        // discarding whatever the user was working on.
        this.verboseLog('Broker token renewal failed -- interaction required');
        console.error(err);
        throw new AuthInteractionRequiredError();
      }
    }

    if (this.msalInstance === null) {
      throw new Error(`No MSAL instance for ${this.appName} -- log in before trying to get an access token!`);
    }

    // throw if an active account has not been set
    const account = this.msalInstance.getActiveAccount();
    if (!account) {
      throw new Error('No active account has been set');
    }

    let response: AuthenticationResult | undefined = undefined;
    let interactionRequired = false;

    // try to get the token silently, but fall back to redirect flow if it fails
    //
    // NOTE/TODO: asynchronous parallel operations (such as sending dicoms for auto-contouring)
    // may cause problems if the current access token (inside authInstance) has timed out and
    // msal has to fall back to acquireTokenRedirect as this will essentially reset the current
    // page during the redirect re-login. This may also cause loss of unsaved data user
    // has in their current session should this happen during user interaction. For the latter there's no real
    // fix possible -- if these turn out to be actual problem then the code must be changed so
    // that current work-in-progress operations are stored in browser memory and can be continued
    // after a login redirect has completed.
    //
    // see also: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/token-lifetimes.md
    try {
      const forceRefreshConfig: Partial<SilentRequest> = {};

      // if first time, force refresh to get a long-lived token
      if (firstTime) {
        forceRefreshConfig.forceRefresh = true;
        forceRefreshConfig.refreshTokenExpirationOffsetSeconds = 10 * 60 * 60; // 10 hours
        forceRefreshConfig.cacheLookupPolicy = CacheLookupPolicy.RefreshToken;
      }

      // skip cache if requested
      if (skipCache) {
        forceRefreshConfig.cacheLookupPolicy = CacheLookupPolicy.Skip;
      }

      response = await this.msalInstance.acquireTokenSilent({ ...this.request, account, redirectUri: TOKEN_REDIRECT_URI, ...forceRefreshConfig });
      if (!response) {
        interactionRequired = true;
      } else {
        this.verboseLog('Got an access token silently');
      }
    } catch (err) {
      console.error(err);
      interactionRequired = true;
    }

    if (interactionRequired) {

      // This code block used to just re-acquire the token directly via redirect auth flow but that will, as implied by
      // the name, redirect user away from their current page, potentially losing any data they're working on (and e.g.
      // attempting to save right now). Now, instead, an error is thrown. This error should be catched higher up in
      // the application (this happens automatically when using the callApi helper in src/store/sagas.ts) and used to
      // mark authSlice to require interaction, which will then show a modal dialog (InteractionRequiredModal.tsx)
      // that will open this same application in a new tab or window. This new tab/window will perform a new full
      // auth flow which gets a new refresh token which can then immediately be re-used in the original tab/window.

      this.verboseLog('Getting token silently failed -- throwing an error to signal that auth interaction is required via a new tab/window');
      throw new AuthInteractionRequiredError();
    }

    if (!response) {
      throw new Error('Could not get access token');
    }

    return response.accessToken as string;
  }

  getLoggedInUserDetails(): LoggedInMsalUser | undefined {
    // On the broker path the details are resolved during logIn (the provider's
    // lookup is async, this accessor is not) and cached on loggedInUser.
    if (this.getOidcProvider()) { return this.loggedInUser; }

    if (!this.msalInstance) { return undefined; }

    const account = this.msalInstance.getActiveAccount();
    if (!account) { return undefined; }

    return {
      userId: account.localAccountId,
      email: account.username,
      // name: account.name
      name: account.idTokenClaims ? account.idTokenClaims.preferred_username ? account.idTokenClaims.preferred_username : account.name : account.name,
      fullName: account.name || account.username,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async fetch(url: string, httpRequestOptions: any = undefined, backendFetchOptions: Partial<BackendFetchOptions> = {}): Promise<Response> {

    const backendOptions = defaultBackendFetchOptions(backendFetchOptions);

    if (!this.isLoggedIn) {
      throw new Error(`Must be logged into ${this.appName} before calling any MSAL APIs!`);
    }

    const token = await this.getAccessToken();

    const fetchOptions = httpRequestOptions || {};
    const bearer = `Bearer ${token}`;
    set(fetchOptions, 'headers.Authorization', bearer);

    if (!backendOptions.skipDefaultHeaders) {
      if (!backendOptions.allowCache) {
        set(fetchOptions, 'headers.Cache-Control', 'no-store');
        set(fetchOptions, 'cache', 'no-store');
        set(fetchOptions, 'headers.pragma', 'no-cache');
      }

      // set app version so backend knows which app and which version of it is sending the request
      const appVersionHeader = getAppVersionHeader(this._appVersion);
      set(fetchOptions, 'headers.appVersion', appVersionHeader);
    }

    // set content type as JSON if it's not already set & we're POSTing JSON
    if (backendFetchOptions.postJson && !has(fetchOptions, 'headers.Content-Type')) {
      set(fetchOptions, 'headers.Content-Type', 'application/json');
    }

    const fullUrl = new URL(url);

    if (!backendFetchOptions.noClientId) {
      // append client ID to query parameters
      const clientIdQueryParam = `${QUERY_PARAM_SESSION_ID}=${getSessionId()}`;
      fullUrl.search = fullUrl.search ? `${fullUrl.search}&${clientIdQueryParam}` : clientIdQueryParam;
    }

    const fetchTimeoutInMs = backendOptions.fetchTimeoutInMs;
    return await retryWithBackoff(
      () => {
        if (fetchTimeoutInMs !== undefined) {
          set(fetchOptions, 'signal', timeoutSignal(fetchTimeoutInMs));
        }
        return fetch(fullUrl.toString(), fetchOptions);
      },
      backendOptions.retry,
      (attempt, err, delayMs) => {
        console.log(`An error occurred when trying to fetch from ${url}`);
        console.log('Fetch options:');
        console.log({ ...fetchOptions, headers: { ...fetchOptions.headers, Authorization: '[REDACTED]' } });
        console.log('Fetch error:');
        console.log(err);
        console.warn(`Attempting retry ${attempt}/${backendOptions.retry.maxRetries} to ${url} (waiting ${delayMs}ms)`);
      },
      () => {
        if (fetchOptions && get(fetchOptions, 'signal.reason.name', undefined) === 'TimeoutError') {
          throw new Error('Request timed out.');
        }
      },
    );
  }

  private setLoggedIn() {
    this.isLoggedIn = true;
    this.loggedInUser = this.getLoggedInUserDetails();
  }

}
